/**
 * Decides whether Browse's filter/reset effect (src/pages/LatestPedals.jsx)
 * should clear the grid and reload from the top.
 *
 * `userWantedPedals` only affects the Wanted tab: it is read in the batch
 * loop's `filterType === 'wanted'` branch and in that tab's empty-state copy,
 * nowhere else. Yet it sits in the effect's dependency list, so for any
 * signed-in member with at least one want, the wants resolving after mount
 * (or after auth restores on a cold load) used to clear the All grid and
 * restart the whole first-page sequence: listing batches, owner profiles,
 * cursor read, with the first run's results discarded (2026-10-26 Browse
 * review). When the ONLY change is that array and the tab is not Wanted, the
 * current grid is already correct and must be left alone.
 *
 * Everything else keeps the shipped behaviour: a user-initiated change always
 * reloads (it wins even if wants changed in the same render), the initial
 * mount reloads, and the Wanted tab reloads whenever its wants change.
 */
export function shouldReloadBrowse({ userInitiatedChange, userWantedPedalsChanged, filterType }) {
  if (userInitiatedChange) return true;
  if (userWantedPedalsChanged && filterType !== 'wanted') return false;
  return true;
}

/**
 * May an APPEND (load more) run now? A reset always may. An append needs a
 * cursor once cards are on screen: after Back-navigation restoration the
 * restored cards are in state immediately while the cursor behind them is
 * still being resolved, and an append without it would run batch 1 again and
 * re-fetch listings already shown (2026-10-31). With no cards at all, the
 * first page legitimately has no cursor.
 */
export function canAppendBrowse({ reset, hasPedals, hasCursor }) {
  if (reset) return true;
  if (hasPedals && !hasCursor) return false;
  return true;
}

/**
 * Should one Browse load stop scanning further batches and hand back what it
 * found so far? The loader fetches 50-listing batches until 28 pass the
 * active filters. A text search ("brothers") matches few listings, so a
 * single load could scan the whole marketplace batch by batch; on the phone's
 * one-request-at-a-time transport that ran past the 10 s whole-load timeout
 * and surfaced as "Couldn't load pedals" even though matches existed
 * (2026-10-31). Each load now stops after `maxBatches` batches or `budgetMs`
 * and reports has-more, so the next page continues the scan from the cursor.
 */
export function shouldPauseScan({ found, target, batches, elapsedMs, maxBatches = 8, budgetMs = 6000 }) {
  if (found >= target) return false;
  return batches >= maxBatches || elapsedMs >= budgetMs;
}
